/**
 * Indicative UK lifetime annuity rates.
 *
 * Annual income per £100,000 purchased, healthy life, 10-year guarantee,
 * provider average (L&G, August 2026). Deliberately NOT the best-buy figure.
 * The 3% escalating column is derived (0.70 × level, checked against Which?
 * best-buy ratios of 0.69 at 65 and 0.72 at 70).
 *
 * Shown in the wizard as a starting point only — the user is expected to
 * enter their own quote. Rates move with gilt yields, so the "as of" date is
 * displayed next to every figure.
 */

export type AnnuityEscalation = 'LEVEL' | 'FIXED' | 'RPI';

/** Month the table was taken from provider quotes (YYYY-MM). Shown in the UI. */
export const ANNUITY_RATES_AS_OF = '2026-08';

/** Human-readable form of {@link ANNUITY_RATES_AS_OF} for captions. */
export const ANNUITY_RATES_AS_OF_LABEL = 'August 2026';

export interface AnnuityRateRow {
  age: number;
  /** Single life, level — per £100,000 */
  singleLevel: number;
  /** Joint life 50% survivor, level — per £100,000 */
  jointLevel: number;
  /** Single life, RPI-linked — per £100,000 */
  singleRpi: number;
  /** Joint life 50% survivor, RPI-linked — per £100,000 */
  jointRpi: number;
  /** Single life, 3% fixed escalation — per £100,000 (derived, 0.70 × level) */
  singleFixed: number;
}

export const ANNUITY_RATE_TABLE: readonly AnnuityRateRow[] = [
  { age: 55, singleLevel: 6376, jointLevel: 6030, singleRpi: 3888, jointRpi: 3620, singleFixed: 4460 },
  { age: 60, singleLevel: 6663, jointLevel: 6369, singleRpi: 4328, jointRpi: 3975, singleFixed: 4660 },
  { age: 65, singleLevel: 7280, jointLevel: 6646, singleRpi: 4915, jointRpi: 4394, singleFixed: 5100 },
  { age: 70, singleLevel: 7847, jointLevel: 7231, singleRpi: 5465, jointRpi: 5034, singleFixed: 5650 },
  { age: 75, singleLevel: 8640, jointLevel: 7877, singleRpi: 6437, jointRpi: 5717, singleFixed: 6220 },
];

export const ANNUITY_RATE_MIN_AGE = ANNUITY_RATE_TABLE[0].age;
export const ANNUITY_RATE_MAX_AGE = ANNUITY_RATE_TABLE[ANNUITY_RATE_TABLE.length - 1].age;

/** Linear interpolation of one column at `age`, clamped to the table's age range. */
function interpolateColumn(age: number, column: keyof Omit<AnnuityRateRow, 'age'>): number {
  const clamped = Math.min(ANNUITY_RATE_MAX_AGE, Math.max(ANNUITY_RATE_MIN_AGE, age));
  let lower = ANNUITY_RATE_TABLE[0];
  let upper = ANNUITY_RATE_TABLE[ANNUITY_RATE_TABLE.length - 1];
  for (let i = 0; i < ANNUITY_RATE_TABLE.length - 1; i++) {
    if (clamped >= ANNUITY_RATE_TABLE[i].age && clamped <= ANNUITY_RATE_TABLE[i + 1].age) {
      lower = ANNUITY_RATE_TABLE[i];
      upper = ANNUITY_RATE_TABLE[i + 1];
      break;
    }
  }
  if (upper.age === lower.age) return lower[column];
  const t = (clamped - lower.age) / (upper.age - lower.age);
  return lower[column] + t * (upper[column] - lower[column]);
}

/**
 * Indicative annual annuity income per £100 purchased, to 2 decimal places.
 *
 * - Interpolates linearly between table rows; clamps at 55 and 75.
 * - Joint-life 3% escalating is not quoted directly: it is single escalating
 *   scaled by the joint ÷ single level ratio at that age.
 */
export function indicativeAnnuityRate(age: number, jointLife: boolean, escalation: AnnuityEscalation): number {
  let per100k: number;
  switch (escalation) {
    case 'LEVEL':
      per100k = interpolateColumn(age, jointLife ? 'jointLevel' : 'singleLevel');
      break;
    case 'RPI':
      per100k = interpolateColumn(age, jointLife ? 'jointRpi' : 'singleRpi');
      break;
    case 'FIXED': {
      const singleFixed = interpolateColumn(age, 'singleFixed');
      if (!jointLife) {
        per100k = singleFixed;
      } else {
        const singleLevel = interpolateColumn(age, 'singleLevel');
        const jointLevel = interpolateColumn(age, 'jointLevel');
        per100k = singleFixed * (jointLevel / singleLevel);
      }
      break;
    }
    default: {
      const exhaustive: never = escalation;
      throw new Error(`Unknown annuity escalation: ${String(exhaustive)}`);
    }
  }
  return Math.round(per100k / 1000 * 100) / 100;
}
